import { useEffect, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { SiteAlertBanner } from "@/components/SiteAlertBanner";
import { CookieConsent } from "@/components/consent/CookieConsent";


import { COMPLIANCE_MODE } from "@/data/site";

// Lazy-load the floating assistant widget — it's below-the-fold and contributes
// significantly to main-bundle size, hurting FCP/LCP. Loading it on demand
// keeps the initial JS payload smaller without changing UX.
const AskCwsWidget = lazy(() =>
  import("@/components/assistant/AskCwsWidget").then((m) => ({ default: m.AskCwsWidget })),
);
import { trackPublicPageView } from "@/lib/track";
import { analyticsAllowed, CONSENT_CHANGED_EVENT, functionalAllowed, getConsent } from "@/lib/consent";
import {
  isPublicMarketingPath,
  loadGa4Once,
  loadGtmOnce,
  gaPageView,
  isGaConfigured,
  isGtmConfigured,
  setConsentDefaults,
  updateConsentMode,
} from "@/lib/analytics-loader";
import { useState } from "react";

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { pathname, hash, key } = location;
  const [functionalOk, setFunctionalOk] = useState<boolean>(() => functionalAllowed());
  // Defer mounting the assistant widget chunk until the browser is idle so it
  // doesn't compete with FCP/LCP for network and main-thread time.
  const [widgetReady, setWidgetReady] = useState(false);
  useEffect(() => {
    if (!functionalOk) return;
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    const schedule = w.requestIdleCallback
      ? w.requestIdleCallback(() => setWidgetReady(true), { timeout: 4000 })
      : window.setTimeout(() => setWidgetReady(true), 2500);
    return () => {
      const cancel = (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (cancel) cancel(schedule as number);
      else clearTimeout(schedule as number);
    };
  }, [functionalOk]);

  // Set Consent Mode v2 defaults once on mount
  useEffect(() => {
    setConsentDefaults();
    const c = getConsent();
    if (c) updateConsentMode({ analytics: c.analytics, functional: c.functional, advertising: c.advertising });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      trackPublicPageView(pathname);
      if (isPublicMarketingPath(pathname) && analyticsAllowed()) {
        if (isGaConfigured()) {
          loadGa4Once();
          gaPageView(pathname);
        }
        if (isGtmConfigured()) loadGtmOnce();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [pathname]);

  // Load analytics retroactively if user opts in during the session
  useEffect(() => {
    const onChange = () => {
      const c = getConsent();
      if (c) updateConsentMode({ analytics: c.analytics, functional: c.functional, advertising: c.advertising });
      setFunctionalOk(c?.functional === true);
      if (!analyticsAllowed()) return;
      if (!isPublicMarketingPath(pathname)) return;
      if (isGaConfigured()) {
        loadGa4Once();
        gaPageView(pathname);
      }
      if (isGtmConfigured()) loadGtmOnce();
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, onChange);
  }, [pathname]);

  // Re-run on every navigation (including same-URL clicks) via location.key
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const scrollToEl = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return true;
        }
        return false;
      };
      if (scrollToEl()) return;
      // Retry after content mounts
      const t1 = setTimeout(scrollToEl, 50);
      const t2 = setTimeout(scrollToEl, 300);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [key, pathname, hash]);

  // Force same-URL clicks to re-trigger navigation (so scroll re-runs even
  // when user clicks a link pointing to the page/section they're already on).
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/")) return;
      // Parse target path + hash
      const [tPath, tHashRaw] = href.split("#");
      const tHash = tHashRaw ? `#${tHashRaw}` : "";
      if (tPath !== pathname) return; // different route -- let router handle
      // Same path: re-navigate to force scroll effect to re-run
      e.preventDefault();
      navigate(href, { replace: false });
      // If hash is identical, navigate() may not bump key; manually scroll/refresh
      if (tHash === hash) {
        if (tHash) {
          const el = document.getElementById(tHash.slice(1));
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [pathname, hash, navigate]);

  return (
    <div className="public-site flex min-h-screen flex-col bg-background">
      {/* Skip link: first focusable element on every public page, visible only on focus. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:inline-flex focus:min-h-11 focus:items-center focus:rounded-md focus:bg-[hsl(var(--primary-deep))] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-none focus:ring-2 focus:ring-[hsl(140_70%_72%)] focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <SiteAlertBanner />
      <Header />
      <main id="main-content" className="flex-1 overflow-x-clip">{children}</main>
      <Footer />
      {false && !COMPLIANCE_MODE && functionalOk && widgetReady && (
        <Suspense fallback={null}>
          <AskCwsWidget />
        </Suspense>
      )}
      <CookieConsent />
    </div>
  );
};
